import LondonCalendarPage from './LondonCalendarPage';
import { getCurrentUserInfo } from '../../utils/auth';
// src/pages/Calendar/index.js
import React, { useState, useCallback, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert, 
  ButtonGroup, 
  Button,
  IconButton,
  Popover,
  Chip
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { useAppTheme } from '../../contexts/ThemeContext';
import { getTenantCacheKey } from '../../utils/auth';
import { 
  getCalendarLimits, 
  getCalendarDateRange, 
  isDateAllowedForTier,
  shouldShowUpgradePrompts,
  getUpgradeMessage
} from '../../utils/subscription';

// Import API service - this should be two levels up from pages/Calendar
import appointmentsApi from '../../api/appointments';
import { formatClientName } from '../../utils/displayNames';

// Import calendar components
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../styles/calendar-theme.css';
import AppointmentFormDialog from './components/AppointmentFormDialog';
import AppointmentWriteDialog from './components/AppointmentWriteDialog';
import CalendarEvent from './components/CalendarEvent';
import AppointmentFilters from './components/AppointmentFilters';
import DevOnly from '../../components/DevOnly';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { isInternalPlanUser } from '../../utils/featureAccess';

// Setup the localizer for BigCalendar
const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

// Format date for API
const formatDateForApi = (date) => {
  return date.toISOString().split('T')[0]; // YYYY-MM-DD
};

const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState('month');
  const [appointmentFormOpen, setAppointmentFormOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [filters, setFilters] = useState({});
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  
  // Get current theme and subscription limits
  const { currentTheme } = useAppTheme();
  const calendarLimits = getCalendarLimits();
  const showUpgrades = shouldShowUpgradePrompts();
  
  console.log('🗓️ Calendar limits:', calendarLimits);
  
  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Memoize the filters object to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => {
    return filters;
  }, [
    filters?.user_ids?.join(',') || '',
    filters?.location_ids?.join(',') || '',
    filters?.showUnassigned,
    Object.keys(filters || {}).length
  ]);

  // Handle date picker
  const handleDatePickerClick = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (newDate) => {
    if (newDate) {
      setCurrentDate(newDate);
      handleDatePickerClose();
    }
  };

  const datePickerOpen = Boolean(datePickerAnchor);

  // Calculate the date range based on view type and tier limitations
  const { startDate, endDate, tierInfo } = useMemo(() => {
    let requestedStart, requestedEnd;
    
    if (viewType === 'day') {
      requestedStart = new Date(currentDate);
      requestedStart.setHours(0, 0, 0, 0);
      requestedEnd = new Date(currentDate);
      requestedEnd.setHours(23, 59, 59, 999);
    } 
    else if (viewType === 'week') {
      requestedStart = new Date(currentDate);
      requestedStart.setDate(currentDate.getDate() - currentDate.getDay()); // Start of week (Sunday)
      requestedStart.setHours(0, 0, 0, 0);
      
      requestedEnd = new Date(requestedStart);
      requestedEnd.setDate(requestedStart.getDate() + 6); // End of week (Saturday)
      requestedEnd.setHours(23, 59, 59, 999);
    }
    else if (viewType === 'agenda') {
      // For agenda view, limit to 2 weeks instead of full month
      requestedStart = new Date(currentDate);
      requestedStart.setHours(0, 0, 0, 0);
      requestedEnd = new Date(currentDate);
      requestedEnd.setDate(requestedEnd.getDate() + 14); // Next 2 weeks only
      requestedEnd.setHours(23, 59, 59, 999);
    }
    else {
      // Default to month view - only current month
      requestedStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      requestedEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      requestedEnd.setHours(23, 59, 59, 999);
    }
    
    // Apply tier-based date range filtering
    const tierRange = getCalendarDateRange(requestedStart, requestedEnd);
    
    // For limited tiers, use the tier-constrained range
    // For unlimited tiers, use the requested range
    if (calendarLimits.is_limited) {
      return {
        startDate: tierRange.startDate,
        endDate: tierRange.endDate,
        tierInfo: {
          wasLimited: tierRange.wasLimited,
          tier: tierRange.tier,
          originalStart: requestedStart,
          originalEnd: requestedEnd
        }
      };
    } else {
      return {
        startDate: requestedStart,
        endDate: requestedEnd,
        tierInfo: {
          wasLimited: false,
          tier: tierRange.tier
        }
      };
    }
  }, [currentDate, viewType, calendarLimits]); // Include calendarLimits in dependencies

  // Memoize the formatted date strings for stable query keys
  const formattedStartDate = useMemo(() => formatDateForApi(startDate), [startDate]);
  const formattedEndDate = useMemo(() => formatDateForApi(endDate), [endDate]);
  
  // Fetch appointments based on date range and filters (optimized caching)
  const { 
    data: appointments,
    isLoading, 
    error,
    refetch
  } = useQuery({
    queryKey: [getTenantCacheKey('appointments'), formattedStartDate, formattedEndDate, viewType, memoizedFilters],
    queryFn: () => appointmentsApi.getAppointments(
      formattedStartDate, 
      formattedEndDate,
      memoizedFilters
    ),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes instead of 5
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if cache is valid
    keepPreviousData: true // Keep previous data while loading new data
  });

  // Format appointments for the calendar (memoized to prevent infinite loops)
  const events = useMemo(() => {
    if (!appointments) return [];

    // Apply unassigned filter if showUnassigned is false
    let filteredAppointments = appointments;
    if (memoizedFilters.showUnassigned === false) {
      filteredAppointments = appointments.filter(appt => {
        // Keep only appointments with client_id (filter out unassigned)
        return appt.client_id !== null && appt.client_id !== undefined;
      });
    }

    return filteredAppointments.map(appointment => {
      const clientName = formatClientName(appointment, 'Appointment');
      const title = clientName === null ? '⚠️ Unassigned Client' : clientName;

      return {
        id: appointment.id,
        title: title,
        start: new Date(appointment.start_date),
        end: new Date(appointment.end_date),
        resource: appointment, // Store the original appointment data
        style: {}
      };
    });
  }, [appointments, currentTheme, memoizedFilters.showUnassigned]);

  // Status-based coloring for events
  const getStatusColors = useCallback((status) => {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'complete':
        return { bg: '#2e7d32', text: '#ffffff' };
      case 'no-show':
      case 'no_show':
        return { bg: '#c62828', text: '#ffffff' };
      case 'waiting':
      case 'scheduled':
      default:
        return { bg: currentTheme.primary, text: '#ffffff' };
    }
  }, [currentTheme]);

  // Custom event style getter for the calendar (memoized)
  const eventStyleGetter = useCallback((event, start, end, isSelected) => {
    const { bg, text } = getStatusColors(event?.resource?.status);
    return {
      style: {
        backgroundColor: bg,
        borderColor: bg,
        color: text,
        border: 'none',
        borderRadius: '4px',
        opacity: isSelected ? 1 : 0.95,
        fontSize: '0.85rem',
        padding: '2px 4px'
      }
    };
  }, [getStatusColors]);

  // Custom calendar styles based on theme (memoized)
  const calendarStyles = useMemo(() => ({
    '& .rbc-calendar': {
      fontFamily: 'Roboto, Arial, sans-serif',
    },
    '& .rbc-header': {
      backgroundColor: `${currentTheme.primary}15`,
      borderColor: `${currentTheme.primary}30`,
      color: currentTheme.primary,
      fontWeight: 600,
      padding: '12px 8px'
    },
    '& .rbc-today': {
      backgroundColor: `${currentTheme.primary}10`,
    },
    '& .rbc-off-range-bg': {
      backgroundColor: '#f8f9fa',
    },
    '& .rbc-event': {
      backgroundColor: currentTheme.primary,
      borderColor: currentTheme.primary,
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '0.85rem',
      padding: '2px 4px'
    },
    '& .rbc-event:hover': {
      backgroundColor: currentTheme.secondary,
      borderColor: currentTheme.secondary,
    },
    '& .rbc-selected': {
      backgroundColor: currentTheme.secondary,
      borderColor: currentTheme.secondary,
    },
    '& .rbc-slot-selection': {
      backgroundColor: `${currentTheme.primary}20`,
    },
    '& .rbc-day-slot .rbc-time-slot': {
      borderColor: '#e0e0e0',
    },
    '& .rbc-time-view .rbc-time-gutter .rbc-time-slot': {
      borderColor: '#e0e0e0',
    },
    '& .rbc-month-view': {
      border: `1px solid ${currentTheme.primary}30`,
      borderRadius: '8px',
      overflow: 'hidden'
    },
    '& .rbc-time-view': {
      border: `1px solid ${currentTheme.primary}30`,
      borderRadius: '8px',
      overflow: 'hidden'
    },
    '& .rbc-agenda-view': {
      border: `1px solid ${currentTheme.primary}30`,
      borderRadius: '8px',
      overflow: 'hidden'
    },
    '& .rbc-agenda-view .rbc-agenda-content': {
      backgroundColor: 'white'
    },
    '& .rbc-agenda-view .rbc-agenda-date-cell': {
      backgroundColor: `${currentTheme.primary}15`,
      color: currentTheme.primary,
      fontWeight: 600
    },
    '& .rbc-agenda-view .rbc-agenda-time-cell': {
      backgroundColor: `${currentTheme.primary}15`,
      color: currentTheme.primary,
      fontWeight: 600
    }
  }), [currentTheme]);

  // Handle time slot selection (creating a new appointment)
  const handleSelectSlot = ({ start, end }) => {
    // Only allow internal plan users to create appointments
    if (!isInternalPlanUser()) {
      console.warn('Write action blocked: only internal plan can create appointments');
      return;
    }
    
    // Pass the correct structure for the dialog
    setSelectedTimeSlot({
      start: start,
      end: end
    });
    setSelectedAppointment(null);
    setAppointmentFormOpen(true);
  };
  
  // Handle appointment selection (viewing/editing an existing appointment)
  const handleSelectEvent = (event) => {
    // Allow viewing appointments for all users
    setSelectedAppointment(event.resource);
    setSelectedTimeSlot(null);
    setAppointmentFormOpen(true);
  };
  
  // Handle saving appointment
  const handleSaveAppointment = () => {
    refetch();
    setAppointmentFormOpen(false);
  };
  
  // Navigation functions for different view types with tier restrictions
  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    
    switch (viewType) {
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
      case 'agenda':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      default:
        newDate.setDate(newDate.getDate() - 1);
    }
    
    // Check if the new date is allowed for the user's tier
    if (calendarLimits.is_limited && !isDateAllowedForTier(newDate)) {
      console.log('🚫 Navigation blocked by tier restrictions');
      // Could show a toast or alert here
      return;
    }
    
    setCurrentDate(newDate);
  };
  
  const navigateNext = () => {
    const newDate = new Date(currentDate);
    
    switch (viewType) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'month':
      case 'agenda':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      default:
        newDate.setDate(newDate.getDate() + 1);
    }
    
    // Check if the new date is allowed for the user's tier
    if (calendarLimits.is_limited && !isDateAllowedForTier(newDate)) {
      console.log('🚫 Navigation blocked by tier restrictions');
      // Could show a toast or alert here
      return;
    }
    
    setCurrentDate(newDate);
  };
  
  // Check if navigation buttons should be disabled
  const isPreviousDisabled = useMemo(() => {
    if (!calendarLimits.is_limited) return false;
    
    const testDate = new Date(currentDate);
    switch (viewType) {
      case 'day':
        testDate.setDate(testDate.getDate() - 1);
        break;
      case 'week':
        testDate.setDate(testDate.getDate() - 7);
        break;
      case 'month':
      case 'agenda':
        testDate.setMonth(testDate.getMonth() - 1);
        break;
      default:
        testDate.setDate(testDate.getDate() - 1);
    }
    
    return !isDateAllowedForTier(testDate);
  }, [calendarLimits.is_limited, currentDate, viewType]);
  
  const isNextDisabled = useMemo(() => {
    if (!calendarLimits.is_limited) return false;
    
    const testDate = new Date(currentDate);
    switch (viewType) {
      case 'day':
        testDate.setDate(testDate.getDate() + 1);
        break;
      case 'week':
        testDate.setDate(testDate.getDate() + 7);
        break;
      case 'month':
      case 'agenda':
        testDate.setMonth(testDate.getMonth() + 1);
        break;
      default:
        testDate.setDate(testDate.getDate() + 1);
    }
    
    return !isDateAllowedForTier(testDate);
  }, [calendarLimits.is_limited, currentDate, viewType]);
  
  // Memoize the display date string to prevent re-renders
  const displayDateString = useMemo(() => {
    if (viewType === 'day') {
      return currentDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } else if (viewType === 'week') {
      const startOfWeekDate = new Date(currentDate);
      startOfWeekDate.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeekDate = new Date(startOfWeekDate);
      endOfWeekDate.setDate(startOfWeekDate.getDate() + 6);
      return `${startOfWeekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
  }, [currentDate, viewType]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Calendar Header */}
        {currentTheme.isGradient ? (
          <Box className="page-header-gradient" sx={{ mb: 4 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
              Appointment Calendar
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Manage your appointments and schedule
            </Typography>
          </Box>
        ) : (
          <Typography variant="h4" sx={{ mb: 3 }}>
            Appointment Calendar
          </Typography>
        )}
        
        <AppointmentFilters onFiltersChange={handleFiltersChange} />
        
        {/* Tier Information and Upgrade Prompt */}
        {showUpgrades && calendarLimits.is_limited && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                startIcon={<UpgradeIcon />}
                onClick={() => window.open('/pricing', '_blank')}
              >
                Upgrade
              </Button>
            }
          >
            {getUpgradeMessage()}
          </Alert>
        )}
        
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5">
                {displayDateString}
              </Typography>
              
              {/* Tier limitation chip */}
              {calendarLimits.is_limited && (
                <Chip 
                  label={`${calendarLimits.tier.toUpperCase()} - 4 days view`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              
              {/* Navigation arrows for all views */}
              <Box sx={{ display: 'flex', ml: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={navigatePrevious}
                  disabled={isPreviousDisabled}
                  sx={{ minWidth: 'auto', px: 1 }}
                  title={isPreviousDisabled ? 'Navigation limited by subscription tier' : 'Previous'}
                >
                  ‹
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={navigateNext}
                  disabled={isNextDisabled}
                  sx={{ minWidth: 'auto', px: 1, ml: 1 }}
                  title={isNextDisabled ? 'Navigation limited by subscription tier' : 'Next'}
                >
                  ›
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Quick Date Navigation */}
              <IconButton
                onClick={handleDatePickerClick}
                size="small"
                sx={{ 
                  mr: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
                title="Jump to date"
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
              
              <Popover
                open={datePickerOpen}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Jump to date
                  </Typography>
                  <DatePicker
                    value={currentDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'outlined'
                      }
                    }}
                  />
                </Box>
              </Popover>
              
              <ButtonGroup sx={{ mr: 2 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    today.setMonth(today.getMonth() - 3);
                    setCurrentDate(today);
                    setViewType('6months');
                  }}
                  title="View past 3 months"
                >
                  PAST
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setCurrentDate(new Date())}
                >
                  TODAY
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => {
                    const today = new Date();
                    setCurrentDate(today);
                    setViewType('6months');
                  }}
                  title="View upcoming 3 months"
                >
                  UPCOMING
                </Button>
              </ButtonGroup>
              <ButtonGroup>
                <Button 
                  variant={viewType === 'day' ? 'contained' : 'outlined'} 
                  onClick={() => setViewType('day')}
                >
                  DAY
                </Button>
                <Button 
                  variant={viewType === 'week' ? 'contained' : 'outlined'} 
                  onClick={() => setViewType('week')}
                >
                  WEEK
                </Button>
                <Button 
                  variant={viewType === 'month' ? 'contained' : 'outlined'} 
                  onClick={() => setViewType('month')}
                >
                  MONTH
                </Button>
                <Button 
                  variant={viewType === 'agenda' ? 'contained' : 'outlined'} 
                  onClick={() => setViewType('agenda')}
                >
                  AGENDA
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error loading appointments: {error.message}
            </Alert>
          ) : (
            <Box sx={{ 
              height: 600,
              ...calendarStyles
            }}>
              {/* Use agenda view for 6months and year to show list format */}
              {(viewType === '6months' || viewType === 'year') ? (
                <Box sx={{ 
                  height: '100%', 
                  overflow: 'auto',
                  border: `1px solid ${currentTheme.primary}30`,
                  borderRadius: '8px',
                  p: 2
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: currentTheme.primary }}>
                    {events.length} appointments in range
                  </Typography>
                  {events.length === 0 ? (
                    <Typography color="text.secondary">No appointments found in this period</Typography>
                  ) : (
                    events.map(event => (
                      <Box 
                        key={event.id}
                        onClick={() => handleSelectEvent(event)}
                        sx={{ 
                          p: 1.5, 
                          mb: 1, 
                          borderRadius: 1,
                          backgroundColor: `${currentTheme.primary}10`,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: `${currentTheme.primary}20`
                          }
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {event.start.toLocaleDateString('en-US', { 
                            weekday: 'short',
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {' • '}
                          {event.start.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit'
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {event.title}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              ) : (
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  view={viewType === '6months' || viewType === 'year' ? 'month' : viewType}
                  date={currentDate}
                  onNavigate={date => setCurrentDate(date)}
                  onView={view => {
                    if (view !== '6months' && view !== 'year') {
                      setViewType(view);
                    }
                  }}
                  selectable
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSelectSlot}
                  views={['month', 'week', 'day', 'agenda']}
                  toolbar={false}
                  eventStyleGetter={eventStyleGetter}
                  popup
                  popupOffset={{ x: 10, y: 18 }}
                  showMultiDayTimes={false}
                  step={30}
                  components={{
                    event: CalendarEvent,
                    agenda: { event: CalendarEvent }
                  }}
                />
              )}
            </Box>
          )}
        </Paper>
        
        {/* Use write-enabled dialog in dev mode, read-only in production */}
        <DevOnly
          fallback={
            <AppointmentFormDialog 
              open={appointmentFormOpen}
              onClose={() => {
                setAppointmentFormOpen(false);
                setSelectedAppointment(null);
                setSelectedTimeSlot(null);
              }}
              date={selectedTimeSlot}
              appointment={selectedAppointment}
              onSave={handleSaveAppointment}
            />
          }
        >
          <AppointmentWriteDialog 
            open={appointmentFormOpen}
            onClose={() => {
              setAppointmentFormOpen(false);
              setSelectedAppointment(null);
              setSelectedTimeSlot(null);
            }}
            selectedDate={currentDate}
            selectedTimeSlot={selectedTimeSlot}
            appointment={selectedAppointment}
          />
        </DevOnly>
      </Box>
    </LocalizationProvider>
  );
};

export default function CalendarEntry() {
  return getCurrentUserInfo()?.schemaName === 'clinic_ksl' ? <LondonCalendarPage /> : <CalendarPage />;
}
